import { useCallback, useState } from 'react';
import useSWR from 'swr';

import { backendClient } from '@/lib/api-client';

// In-app notifications are sent unconditionally and so are not configurable; the
// preferences endpoint only returns channels the user can change.
export type NotificationChannel = 'email';

export interface NotificationPreferenceItem {
  event_type: string;
  label: string;
  description: string;
  channels: Partial<Record<NotificationChannel, boolean>>;
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreferenceItem[];
}

export interface NotificationPreferenceUpdate {
  eventType: string;
  channel: NotificationChannel;
  enabled: boolean;
}

const NOTIFICATION_PREFERENCES_KEY = '/user/me/notification-preferences';

export const useNotificationPreferences = () => {
  const { data, error, isLoading, mutate } = useSWR<NotificationPreferencesResponse>(
    NOTIFICATION_PREFERENCES_KEY,
    async (url) => {
      const response = await backendClient.get(url);
      return response.data as NotificationPreferencesResponse;
    },
    {
      revalidateOnFocus: true,
      dedupingInterval: 60000,
    },
  );

  const [isPending, setIsPending] = useState(false);
  const [variables, setVariables] = useState<NotificationPreferenceUpdate | undefined>(undefined);

  const mutateAsync = useCallback(
    async ({ eventType, channel, enabled }: NotificationPreferenceUpdate) => {
      setIsPending(true);
      setVariables({ eventType, channel, enabled });
      try {
        const response = await backendClient.patch(NOTIFICATION_PREFERENCES_KEY, {
          preferences: [
            {
              event_type: eventType,
              channels: {
                [channel]: enabled,
              },
            },
          ],
        });
        const updated = response.data as NotificationPreferencesResponse;
        await mutate(updated, { revalidate: false });
        return updated;
      } finally {
        setIsPending(false);
      }
    },
    [mutate],
  );

  const refetch = useCallback(async () => {
    try {
      const result = await mutate();
      return { data: result, error: undefined };
    } catch (err) {
      return { data: undefined, error: err };
    }
  }, [mutate]);

  return {
    preferences: data?.preferences ?? [],
    isLoading,
    isError: Boolean(error),
    error,
    refetch,
    updatePreference: {
      mutateAsync,
      isPending,
      variables,
    },
  };
};
